import { log } from './logger';
import { httpClient } from './api/client';

export type BrandLogoFormat = {
  theme?: string;
  formats?: Array<{ width: number | null; height: number | null; src?: string; format?: string; size?: number; background?: string }>;
  tags?: any[];
  type?: string;
};

export type BrandfetchLink = {
  name: string;
  url: string;
};

export type BrandfetchFormat = {
  src: string;
  format: string;
  height: number;
  width: number;
  size: number;
  background: string;
};

export type BrandfetchLogo = {
  theme?: string;
  formats: BrandfetchFormat[];
  tags?: any[];
  type: string;
};

export type BrandfetchColor = {
  hex: string;
  type: string;
  brightness: number;
};

export type BrandfetchFontWeight = Record<string, any>;

export type BrandfetchFont = {
  name: string;
  type: string;
  origin: string;
  originId: string;
  weights: BrandfetchFontWeight[];
};

export type BrandfetchImage = {
  formats: BrandfetchFormat[];
  tags?: any[];
  type: string;
};

export type BrandfetchIndustryParent = {
  id: string;
  slug: string;
  name: string;
  emoji: string;
};

export type BrandfetchIndustry = {
  id: string;
  score: number;
  slug: string;
  name: string;
  emoji: string;
  parent?: BrandfetchIndustryParent[];
};

export type BrandfetchFinancialIdentifiers = {
  isin?: string[];
  ticker?: string[];
};

export type BrandfetchLocation = {
  city?: string;
  country?: string;
  countryCode?: string;
  region?: string;
  state?: string;
  subregion?: string;
};

export type BrandfetchCompany = {
  employees?: number;
  financialIdentifiers?: BrandfetchFinancialIdentifiers;
  foundedYear?: number;
  industries?: BrandfetchIndustry[];
  kind?: string;
  location?: BrandfetchLocation;
};

export type BrandfetchApiResponse = {
  id: string;
  name: string;
  domain: string;
  claimed: boolean;
  description?: string;
  longDescription?: string;
  links?: BrandfetchLink[];
  logos: BrandfetchLogo[];
  colors?: BrandfetchColor[];
  fonts?: BrandfetchFont[];
  images?: BrandfetchImage[];
  qualityScore?: number;
  company?: BrandfetchCompany;
  isNsfw?: boolean;
  urn?: string;
};

export type BrandLogoResponse = {
  domain: string;
  logo: BrandLogoFormat;
  brand_data?: BrandfetchApiResponse | string;
};

export async function fetchBrand(domain: string) {
  if (!domain) {
    log.warn("fetchBrand called without domain");
    return null;
  }

  try {
    const response = await httpClient.get<BrandLogoResponse>(`/brand/logo/${domain}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    const brandData = response.data;

    if (!brandData?.logo?.formats?.[0]?.src) {
      log.warn("Brand data missing formats", { domain, response: response.data });
      return null;
    }

    let parsedBrandData: BrandfetchApiResponse | null = null;

    if (brandData.brand_data) {
      if (typeof brandData.brand_data === "string") {
        parsedBrandData = JSON.parse(brandData.brand_data) as BrandfetchApiResponse;
      } else {
        parsedBrandData = brandData.brand_data as BrandfetchApiResponse;
      }
    }

    return {
      logoUrl: brandData.logo.formats[0].src,
      logo: brandData.logo,
      brand_data: parsedBrandData,
    };
  } catch (error: any) {
    log.error("Failed to load brand logo", error);
    return null;
  }
}
